if (!customElements.get('subscription-popup')) {
  customElements.define(
    'subscription-popup',
    class SubscriptionPopup extends DrawerComponent {
      static get observedAttributes() {
        return super.observedAttributes.concat(['data-trigger-open', 'data-repeat-open']);
      }

      constructor() {
        super();
      }

      get requiresBodyAppended() {
        return false;
      }

      // Focus the dialog itself on open: no focus ring on the close button for an automatic popup.
      get focusElement() {
        return this.querySelector('.drawer__inner') || this.querySelector('button');
      }

      connectedCallback() {
        super.connectedCallback();

        this.triggerOpen = this.dataset.triggerOpen;
        this.repeatOpen = this.dataset.repeatOpen;
        this.cookieName = 'sleektheme:popup';

        window.FoxTheme.DOMready(this.handleOpen.bind(this));

        window.FoxTheme.utils.addEventDelegate({
          context: this,
          selector: '.btn-apply-coupon-code',
          handler: async (e, target) => {
            e.preventDefault();
            const code = target.dataset.code;
            if (code) this.copyToClipboard(code, target);
          },
        });

        // «Meld deg på» links to #nyhetsbrev on this page open the popup with the e-mail field ready.
        // Without the popup (or with a modifier key) the link still goes to the sign-up in the footer.
        document.addEventListener(
          'click',
          (event) => {
            const link = event.target instanceof Element ? event.target.closest('a[href$="#nyhetsbrev"]') : null;
            if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            const url = new URL(link.getAttribute('href'), window.location.href);
            if (url.pathname !== window.location.pathname) return;
            event.preventDefault();
            this.openForSignup(link);
          },
          { signal: this.abortController.signal }
        );

        // Tab from the focused dialog panel moves into the dialog, not back to the page.
        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Tab' || event.target !== this.focusElement) return;
          const focusable = FoxTheme.a11y.getFocusableElements(this).filter((element) => element !== this.focusElement);
          if (!focusable.length) return;
          event.preventDefault();
          (event.shiftKey ? focusable[focusable.length - 1] : focusable[0]).focus();
        });
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        window.clearTimeout(this.openTimer);
      }

      // With reduced motion the overlay has no transition, so transitionend never fires.
      // Complete the popup lifecycle immediately to restore focus and release the page lock.
      handleShowTransition() {
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return super.handleShowTransition();
        this.setAttribute('active', '');
        return Promise.resolve();
      }

      handleHideTransition() {
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return super.handleHideTransition();
        this.removeAttribute('active');
        return Promise.resolve();
      }

      prepareToShow() {
        super.prepareToShow();
        this.setRepeatOpen();
      }

      async openForSignup(opener = null) {
        await this.show(opener);
        this.querySelector('input[type="email"]')?.focus({ preventScroll: true });
      }

      handleOpen() {
        if (Shopify.designMode) return;

        // After a sign-up in this popup, Shopify returns to the page with the form's anchor.
        // Show the result (code or error) right away, even if the popup was seen before.
        const state = this.dataset.newsletterState;
        const hash = window.location.hash;
        const targetsPopup = !hash || hash.includes('ContactSubscribe-Popup');
        if (state && state !== 'default' && targetsPopup) {
          this.show();
          if (state === 'success') this.markSubscribed();
          this.cleanUrl();
          return;
        }

        // Arriving from another page's «Meld deg på» link.
        if (hash === '#nyhetsbrev') {
          this.openForSignup();
          return;
        }

        if (this.dataset.autoOpen === 'false') return;

        let savedData = FoxTheme.utils.getStorage(this.cookieName);
        if (savedData && savedData.opened) {
          return false;
        }

        switch (this.triggerOpen) {
          case 'delay':
            this.showWhenCalm(5000);
            break;
          case 'scroll_down':
            this.triggerAfterScroll(700, () => this.showWhenCalm(0));
            break;
          default:
            this.showWhenCalm(0);
            break;
        }
      }

      // Never open over another dialog (cart, menus, booking calendar, cookie banner) or while someone is typing.
      isBusy() {
        const body = document.body;
        if (body.classList.contains('modal-show') || body.classList.contains('modal-showing')) return true;
        const active = document.activeElement;
        if (active && active !== body && active.matches('input, textarea, select, [contenteditable="true"]')) return true;
        if (document.querySelector('.header details[open], .header-section details[open]')) return true;
        const booking = document.querySelector('.servicify-modal-portal');
        if (booking && booking.childElementCount > 0 && booking.getClientRects().length > 0) return true;
        // Includes Shopify's cookie banner (role="alertdialog"): wait until the visitor has answered it.
        return Array.from(document.querySelectorAll('[aria-modal="true"], [role="dialog"], [role="alertdialog"], #shopify-pc__banner')).some(
          (dialog) => dialog !== this && !this.contains(dialog) && !dialog.hidden && dialog.getClientRects().length > 0
        );
      }

      showWhenCalm(delay, attempt = 0) {
        window.clearTimeout(this.openTimer);
        this.openTimer = window.setTimeout(() => {
          const savedData = FoxTheme.utils.getStorage(this.cookieName);
          if (savedData && savedData.opened) return;
          if (this.isBusy()) {
            if (attempt < 12) this.showWhenCalm(5000, attempt + 1);
            return;
          }
          this.show();
        }, delay);
      }

      triggerAfterScroll(offset = 500, trigger) {
        let opened = false;
        const start = 0;
        window.addEventListener('scroll', (e) => {
          let scrollTop =
            window.pageYOffset || (document.documentElement || document.body.parentNode || document.body).scrollTop;

          if (scrollTop - start > offset && !opened) {
            trigger();
            opened = true;
            return;
          }
        });
      }

      markSubscribed() {
        window.FoxTheme.utils.setStorage(this.cookieName, { opened: true, subscribed: true }, 365);
      }

      cleanUrl() {
        try {
          const url = new URL(window.location.href);
          // Shopify can echo the address back in the query (contact[email] or a normalised contact=…); never leave it in the address bar.
          ['customer_posted', 'form_type', 'utf8', 'contact', 'contact[email]', 'contact[tags]'].forEach((key) => url.searchParams.delete(key));
          url.hash = '';
          window.history.replaceState(window.history.state, '', url.pathname + url.search);
        } catch (_) { /* Keep the URL as it is. */ }
      }

      copyToClipboard(code, button) {
        navigator.clipboard.writeText(code);
        button.innerText = button.dataset.copiedText;
      }

      setRepeatOpen() {
        if (Shopify.designMode) return;

        let expires;
        switch (this.repeatOpen) {
          case 'no_repeat':
            expires = 365;
            break;
          case 'every_3_mins':
            expires = 3 / 60 / 24;
            break;
          case 'every_5_mins':
            expires = 5 / 60 / 24;
            break;
          case 'every_10_mins':
            expires = 1 / 6 / 24;
            break;
          case 'every_15_mins':
            expires = 15 / 60 / 24;
            break;
          case 'every_30_mins':
            expires = 1 / 2 / 24;
            break;
          case 'every_1_hr':
            expires = 1 / 24;
            break;
          case 'every_6_hrs':
            expires = 6 / 24;
            break;
          case 'every_12_hrs':
            expires = 1 / 2;
            break;
          case 'every_day':
            expires = 1;
            break;
          case 'every_3_days':
            expires = 3;
            break;
          case 'every_week':
            expires = 7;
            break;
          case 'every_2_weeks':
            expires = 14;
            break;
          case 'every_month':
            expires = 30;
            break;
          default:
            expires = 7;
            break;
        }

        window.FoxTheme.utils.setStorage(this.cookieName, { opened: true }, expires);
      }
    }
  );
}
